import { TakerMarket } from '@/enterprise/data/useTakerMarkets';

interface ActiveOfferCountResult {
  /** Number of taker markets with an enabled offer. */
  activeOfferCount: number;
  /**
   * Number of taker markets with a non-PREFERRED_TERM schedule and no enabled offer.
   * PREFERRED_TERM offers are schedule-driven but counted via isEnabled to avoid double-counting.
   */
  activeOfferScheduleCount: number;
  /** Sum of activeOfferCount and activeOfferScheduleCount for display purposes. */
  activeOfferCountDisplay: number;
}

/**
 * Computes active offer counts for a list of taker markets.
 *
 * `activeOfferCount` captures all enabled offers. `activeOfferScheduleCount` captures
 * markets with a non-PREFERRED_TERM schedule that are not already enabled, so each
 * market is counted exactly once in the display total.
 */
const getActiveOfferCount = (takerMarkets: TakerMarket[] = []): ActiveOfferCountResult => {
  const counts = takerMarkets.reduce(
    (acc, takerMarket) => {
      const { isEnabled, offerType } = takerMarket.offerConfig;

      if (isEnabled) {
        acc.activeOfferCount++;
      } else if (takerMarket.offerSchedule && offerType !== 'PREFERRED_TERM') {
        acc.activeOfferScheduleCount++;
      }

      return acc;
    },
    { activeOfferCount: 0, activeOfferScheduleCount: 0 }
  );

  return {
    ...counts,
    activeOfferCountDisplay: counts.activeOfferCount + counts.activeOfferScheduleCount,
  };
};

export default getActiveOfferCount;
